import {
  BOARDS_ID,
  FILTER_FIELDS_TYPES,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';
import { formatDate, sortArray } from '@utils';
import { computeValueByRuleType } from 'components/common/modals/FiltersModalV2/utils';
import { endOfDay, startOfDay } from 'date-fns';
import {
  GENERIC_TICKETS_STATUS,
  GENERIC_TICKET_PRIORITY_OPTIONS,
} from 'components/genericTickets/constants';

export const handleGenericTickets = ({ tickets, teams }) => {
  if (!tickets || !Array.isArray(tickets)) return [];

  return tickets.map((ticket) => ({
    id: ticket?.id || '',
    ticketId: ticket?.id || '',
    ticketType: 'Generic Ticket',
    assignee: ticket?.assignee || '-',
    status: ticket?.status,
    team: teams.find((team) => team.value === ticket?.team)?.label || '-',
    dueDate: ticket?.dueDate
      ? formatDate(new Date(ticket?.dueDate), 'dd/MM/yyyy HH:mm')
      : '',
    statusLabel: GENERIC_TICKETS_STATUS[ticket?.status]?.label || '-',
    userId: ticket?.customFields?.userId || '-',
    username: ticket?.customFields?.userName || '-',
    email: ticket?.customFields?.userEmail || '-',
    phoneNumber: ticket?.customFields?.userPhoneNumber || '-',
    description: ticket?.customFields?.description || '-',
    subscriptionId: ticket?.customFields?.subscriptionId || '',
    bookingId: ticket?.customFields?.bookingId || '',
    createdAt: ticket?.createdAt
      ? formatDate(new Date(ticket?.createdAt), 'dd/MM/yyyy HH:mm')
      : '-',
    completedAt: ticket?.completedAt
      ? formatDate(new Date(ticket?.completedAt), 'dd/MM/yyyy HH:mm')
      : '-',
    priority:
      GENERIC_TICKET_PRIORITY_OPTIONS.find((p) => p.value === ticket?.priority)
        ?.text || GENERIC_TICKET_PRIORITY_OPTIONS[0].text,
    reason: ticket?.reason || '-',
    createdBy: ticket?.createdBy || '-',
  }));
};

export const handleComments = (data = []) => {
  return sortArray(data || [], 'createdAt', 'desc')?.map((entry) => {
    return {
      id: entry?.id,
      date: entry?.createdAt
        ? formatDate(entry?.createdAt, 'dd MMMM yyyy')
        : '-',
      time: entry?.createdAt ? formatDate(entry?.createdAt, 'HH:mm') : '-',
      name: entry?.userFullName || entry?.user || '-',
      comment: entry?.comment,
      edit: entry?.editedAt
        ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
        : '',
    };
  });
};

export const handleGenericTicket = (data, stages = [], teams = []) => {
  if (!data) {
    return {};
  }
  const status = stages.find((s) => s.value === data?.status) || null;

  return {
    ...data,
    status,
    createdAt: data?.createdAt ? formatDate(data.createdAt, 'dd-MM-yyyy') : '-',
    description: data?.ticketDetails?.description || '',
    comments: handleComments(data.comments || []),
    changelog: (data?.changelog || [])
      ?.slice()
      .reverse()
      .map((entry) => {
        if (entry.fieldName === 'team') {
          return {
            ...entry,
            newValue:
              teams.find((team) => team.value === entry.newValue)?.label ||
              entry.newValue,
          };
        }
        if (entry.fieldName === 'priority') {
          return {
            ...entry,
            newValue:
              GENERIC_TICKET_PRIORITY_OPTIONS.find(
                (p) => p.value === entry.newValue
              )?.text || entry.newValue,
          };
        }
        return entry;
      }),
  };
};

// Maps assignee entries to a value/text shape suited to the "Created By" filter:
// value is the email (what the API expects) and text shows both the name and
// the email so users can search by either.
export const toCreatedByOptions = (assignees = []) =>
  assignees
    .filter((a) => a.email)
    .map((a) => ({
      ...a,
      value: a.email,
      text: a.text && a.text !== a.email ? `${a.text} (${a.email})` : a.email,
    }));

const pushIsAnyOfRule = (rules, field, raw, transform = (item) => item) => {
  if (!raw) return;
  // Keep 0 (valid status / priority value); drop only empty strings and nullish.
  const parsedValues = raw
    .split(',')
    .filter((item) => item !== '')
    .map(transform)
    .filter((item) => item !== null && item !== undefined);
  if (!parsedValues.length) return;
  rules.push({
    type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
    field,
    operator: FILTER_OPERATOR_ENUMS['='],
    value: parsedValues,
  });
};

export const getGenericTicketsPayload = (params = {}) => {
  const {
    dateFrom,
    dateTo,
    status,
    assignee,
    team,
    dueDate,
    priority,
    reason,
    createdBy,
  } = params;

  const value = [
    {
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      field: 'boardId',
      operator: FILTER_OPERATOR_ENUMS['='],
      value: BOARDS_ID.GENERIC_TICKETS,
    },
  ];

  pushIsAnyOfRule(value, 'status', status, Number);
  pushIsAnyOfRule(value, 'team', team);
  pushIsAnyOfRule(value, 'priority', priority, Number);
  pushIsAnyOfRule(value, 'reason', reason);
  pushIsAnyOfRule(value, 'createdBy', createdBy);

  if (assignee) {
    value.push({
      field: 'assignee',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: assignee === 'none' ? FILTER_RULE_ENUMS.isEmpty : 5,
      value: assignee === 'none' ? '' : assignee,
    });
  }

  if (dateFrom || dateTo) {
    let startDate;
    let endDate;

    if (!dateFrom) {
      startDate = new Date(0).toISOString(); // Set to epoch start if dateFrom is not provided
      endDate = endOfDay(new Date(dateTo)).toISOString();
    } else if (!dateTo) {
      startDate = startOfDay(new Date(dateFrom)).toISOString();
      endDate = endOfDay(new Date()).toISOString(); // Set to current date if dateTo is not provided
    } else {
      startDate = startOfDay(new Date(dateFrom)).toISOString();
      endDate = endOfDay(new Date(dateTo)).toISOString();
    }

    const dateRule = {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: [
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: 'createdAt',
          operator: FILTER_OPERATOR_ENUMS['>='],
          value: computeValueByRuleType(
            FILTER_RULE_ENUMS.CompareDateRuleType,
            FILTER_FIELDS_TYPES.Date,
            startDate
          ),
        },
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: 'createdAt',
          operator: FILTER_OPERATOR_ENUMS['<='],
          value: computeValueByRuleType(
            FILTER_RULE_ENUMS.CompareDateRuleType,
            FILTER_FIELDS_TYPES.Date,
            endDate
          ),
        },
      ],
    };
    value.push(dateRule);
  }

  if (dueDate) {
    const startDate = new Date(dueDate).toISOString();
    const endDate = new Date(dueDate).toISOString();

    const dateRule = {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: [
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: 'dueDate',
          operator: FILTER_OPERATOR_ENUMS['>='],
          value: computeValueByRuleType(
            FILTER_RULE_ENUMS.CompareDateRuleType,
            FILTER_FIELDS_TYPES.Date,
            startDate
          ),
        },
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: 'dueDate',
          operator: FILTER_OPERATOR_ENUMS['<='],
          value: computeValueByRuleType(
            FILTER_RULE_ENUMS.CompareDateRuleType,
            FILTER_FIELDS_TYPES.Date,
            endDate
          ),
        },
      ],
    };
    value.push(dateRule);
  }

  return {
    field: null,
    operator: null,
    type: FILTER_RULE_ENUMS.AndRuleType,
    value,
  };
};
